import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Product } from "../../../mocks/products";
import { supabase } from "../../../lib/supabase";
import { useTranslatedText, useTranslatedArray } from "../../../hooks/useTranslated";
import DOMPurify from "dompurify";

const isVideoUrl = (url: string) => {
  const ext = url.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
  return ["mp4", "webm", "mov", "avi", "mkv"].includes(ext);
};

interface Props {
  product: Product;
  onInquiryClick?: () => void;
}

const ProductOverview = ({ product, onInquiryClick }: Props) => {
  const { t, i18n } = useTranslation();
  const isKr = i18n.language === "kr";
  const [activeImg, setActiveImg] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [companyLogo, setCompanyLogo] = useState<string>(product.companyLogo ?? "");
  const [companyId, setCompanyId] = useState<string>(product.companyId ?? "");
  const [mainImgError, setMainImgError] = useState(false);

  // Korean auto-translated values
  const displayCategoryName = useTranslatedText(
    product.categoryName,
    product.categoryNameKr,
    isKr
  );
  const displaySubCategoryName = useTranslatedText(
    product.subCategoryName,
    product.subCategoryNameKr,
    isKr
  );
  const displayDescription = useTranslatedText(
    product.description,
    product.descriptionKr,
    isKr
  );
  const displayDetailDesc = useTranslatedText(
    product.descriptionDetail,
    product.descriptionDetailKr,
    isKr
  );
  const displayName = useTranslatedText(
    product.name,
    product.nameKr,
    isKr
  );
  const displayTags = useTranslatedArray(product.tags, product.tagsKr, isKr);

  // Build unified media list from media_items (priority) or images fallback
  const rawMediaItems = (product as unknown as Record<string, unknown>).media_items;
  const mediaItems: Array<{ url: string; type: string; is_cover?: boolean }> = Array.isArray(rawMediaItems)
    ? rawMediaItems as Array<{ url: string; type: string; is_cover?: boolean }>
    : [];

  // Thumbnails: use mediaItems if available, else images
  const thumbList = mediaItems.length > 0
    ? mediaItems.map((m) => ({ url: m.url, isVideo: m.type === "video" || isVideoUrl(m.url), is_cover: m.is_cover }))
    : product.images.map((img) => ({ url: img, isVideo: false, is_cover: false }));

  // Active media for overview display (user can click thumbnails to switch)
  const activeThumb = thumbList[activeImg] ?? thumbList[0];
  const overviewUrl = activeThumb?.url ?? product.images[0] ?? "";
  const overviewIsVideo = activeThumb ? activeThumb.isVideo : false;

  // Debug: log every render
  // eslint-disable-next-line no-console
  console.debug("[ProductOverview] product.id=", product.id, "| activeImg=", activeImg, "| overviewUrl=", overviewUrl, "| thumbList.length=", thumbList.length, "| mediaItems.length=", mediaItems.length, "| images.length=", product.images.length);

  useEffect(() => {
    if (companyLogo && companyId) return;
    if (!product.companyName) return;
    const fetchLogo = async () => {
      const { data } = await supabase
        .from("companies")
        .select("id, logo_url")
        .ilike("name", product.companyName.trim())
        .maybeSingle();
      if (data) {
        if (data.logo_url) setCompanyLogo(data.logo_url);
        if (data.id) setCompanyId(data.id);
      }
    };
    fetchLogo();
  }, [product.companyName, companyLogo, companyId]);

  const shortDesc = displayDescription ?? "";
  const detailDesc = displayDetailDesc ?? "";
  const isDetailHtml = (product.descriptionDetail ?? "").includes("<") || (displayDetailDesc ?? "").includes("<");
  const cleanDetailHtml = isDetailHtml
    ? DOMPurify.sanitize(displayDetailDesc || product.descriptionDetail || "", { ADD_ATTR: ["target", "rel"] })
    : "";

  return (
    <section id="product-info" className="bg-white pt-8 md:pt-14 pb-2 md:pb-4">
      <div className="max-w-[1600px] mx-auto px-3 md:px-5 lg:px-8">
        {/* Main layout: image left + text right — original ratio */}
        <div className="flex flex-col lg:flex-row gap-6 md:gap-12 lg:gap-16 items-start">
          {/* Left: Image gallery */}
          <div className="w-full lg:w-[58%]">
            {/* Main media */}
            <div
              className="relative rounded-2xl overflow-hidden border-2 border-gray-300"
              style={{
                width: "100%",
                backgroundColor: "#F0F4FA",
              }}
            >
              {overviewIsVideo ? (
                <>
                  <video
                    src={overviewUrl}
                    className="w-full h-full object-cover block"
                    autoPlay
                    loop
                    muted={isMuted}
                    playsInline
                    preload="auto"
                    controls
                    poster={product.images[0] ?? ""}
                    style={{ display: "block" }}
                  />
                  {isMuted && (
                    <button
                      type="button"
                      onClick={() => setIsMuted(false)}
                      className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 bg-black/60 hover:bg-black/80 text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors cursor-pointer"
                    >
                      <i className="ri-volume-mute-line" />
                      {isKr ? "소리 켜기" : "Bật tiếng"}
                    </button>
                  )}
                </>
              ) : (
                mainImgError || !overviewUrl ? (
                  <div className="w-full flex flex-col items-center justify-center gap-2" style={{ minHeight: 300, height: 300 }}>
                    <i className="ri-image-line text-5xl text-gray-400" />
                    <p className="text-sm text-gray-500 font-medium">{product.name}</p>
                  </div>
                ) : (
                  <img
                    src={overviewUrl}
                    alt={product.name}
                    className="w-full h-auto block"
                    loading="eager"
                    decoding="sync"
                    style={{
                      display: "block",
                      imageRendering: "-webkit-optimize-contrast",
                      minHeight: 200,
                    }}
                    onLoad={() => {
                      // eslint-disable-next-line no-console
                      console.debug("[ProductOverview] main image loaded:", overviewUrl);
                    }}
                    onError={() => {
                      // eslint-disable-next-line no-console
                      console.error("[ProductOverview] main image failed:", overviewUrl);
                      setMainImgError(true);
                    }}
                  />
                )
              )}
              <div className="absolute top-4 left-4">
                <span className="text-xs font-bold tracking-widest uppercase bg-white/90 backdrop-blur-sm text-[#004A99] px-3 py-1.5 rounded-full">
                  {displaySubCategoryName}
                </span>
              </div>
            </div>
            {/* Thumbnails */}
            {thumbList.length > 1 && (
              <div className="flex gap-2 flex-wrap mt-3">
                {thumbList.map((thumb, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`w-[72px] h-[72px] md:w-20 md:h-20 rounded-xl overflow-hidden border-2 transition-all cursor-pointer flex items-center justify-center relative ${
                      activeImg === i
                        ? "border-[#004A99] ring-2 ring-[#004A99]/20"
                        : "border-gray-300 opacity-70 hover:opacity-100"
                    }`}
                    style={{ backgroundColor: "#E8ECF3" }}
                  >
                    {thumb.isVideo ? (
                      <>
                        <video
                          src={thumb.url}
                          className="w-full h-full object-cover block"
                          muted
                          playsInline
                          preload="metadata"
                          style={{ display: "block" }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <i className="ri-play-circle-fill text-white text-lg" />
                        </div>
                      </>
                    ) : (
                      <img
                        src={thumb.url}
                        alt={`${product.name} ${i + 1}`}
                        className="w-full h-full object-contain block p-0.5"
                        loading="eager"
                        style={{ display: "block", imageRendering: "-webkit-optimize-contrast" }}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                    )}
                    {thumb.is_cover && !thumb.isVideo && (
                      <div className="absolute bottom-0 left-0 right-0 bg-amber-500/80 text-white text-[8px] font-bold text-center py-0.5">
                        Đại diện
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* 2 info boxes in a row below image — full width, no price box */}
            <div className="flex flex-col sm:flex-row gap-4 mt-5 md:mt-6">
              {/* Tags box */}
              <div className="flex-1 bg-gradient-to-r from-[#EEF4FF] to-[#E8F0FF] rounded-xl p-3.5 md:p-4">
                <p className="text-[10px] text-gray-500 mb-1.5 font-semibold uppercase tracking-widest">{isKr ? "키워드" : "Từ khóa"}</p>
                {displayTags && displayTags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {displayTags.slice(0, 5).map((tag) => (
                      <span key={tag} className="text-[10px] bg-white text-[#004A99] px-2 py-0.5 rounded-full font-medium border border-[#004A99]/15 hover:bg-[#004A99] hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                        #{tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-[10px] text-gray-400">—</span>
                )}
              </div>

              {/* Contact box — chỉ hiển thị khi có dữ liệu liên hệ */}
              {(product.managerPhone || product.managerEmail) && (
                <div className="flex-1 bg-[#F8FAFF] rounded-xl p-3.5 md:p-4 border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">{isKr ? "연락처" : "Liên hệ"}</p>
                  <div className="space-y-1">
                    {product.managerPhone ? (
                      <div className="flex items-center gap-1.5">
                        <i className="ri-phone-line text-[#004A99] text-xs" />
                        <span className="text-[10px] text-gray-700 font-medium truncate">{product.managerPhone}</span>
                      </div>
                    ) : null}
                    {product.managerEmail ? (
                      <div className="flex items-center gap-1.5">
                        <i className="ri-mail-line text-[#004A99] text-xs" />
                        <span className="text-[10px] text-gray-700 font-medium truncate">{product.managerEmail}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
            {/* Small gap before story section */}
            <div className="mt-6" />
          </div>

          {/* Right: Product info */}
          <div className="w-full lg:w-[42%]">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-xs font-semibold bg-[#EEF4FF] text-[#004A99] px-3 py-1.5 rounded-full border border-[#004A99]/20">
                {displayCategoryName}
              </span>
              <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
              <span className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-full">
                {displaySubCategoryName}
              </span>
            </div>

            {/* Title */}
            <h2 className="text-3xl font-bold text-gray-900 mb-1 leading-tight">{displayName}</h2>
            <p className="text-gray-400 text-sm mb-4">{product.nameKr || product.name}</p>

            {/* Company */}
            <div className="flex items-center gap-3 py-3 border-y border-gray-100 mb-5">
              <div className="w-11 h-11 flex items-center justify-center bg-[#EEF4FF] rounded-xl flex-shrink-0 overflow-hidden">
                {companyLogo ? (
                  <img
                    src={companyLogo}
                    alt={product.companyName}
                    className="w-full h-full object-contain p-1"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                ) : (
                  <i className="ri-building-4-line text-[#004A99] text-xl" />
                )}
              </div>
              <div>
                <p className="text-xs text-gray-400">{isKr ? "제조 기업" : "Doanh nghiệp cung cấp"}</p>
                {companyId ? (
                  <Link to={`/doanh-nghiep/${companyId}`} className="font-semibold text-gray-900 hover:text-[#004A99] transition-colors cursor-pointer text-sm">
                    {product.companyName}
                  </Link>
                ) : (
                  <p className="font-semibold text-gray-900 text-sm">{product.companyName}</p>
                )}
              </div>
              <span className="ml-auto text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-full font-medium whitespace-nowrap">
                {isKr ? "인증 완료" : "Đã xác thực"}
              </span>
            </div>

            {/* Merged description */}
            <div className="mb-5">
              {displayDescription && (
                <p className="text-gray-700 leading-relaxed text-sm mb-3 font-medium">{displayDescription}</p>
              )}
              {displayDetailDesc && (
                isDetailHtml ? (
                  <div
                    className="prose prose-sm max-w-none text-gray-600 leading-relaxed text-sm
                      [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-4 [&_ul>li]:mb-1
                      [&_ol]:list-decimal [&_ol]:pl-4 [&_ol>li]:mb-1
                      [&_strong]:font-bold [&_strong]:text-gray-800
                      [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mb-2
                      [&_img]:rounded-xl [&_img]:my-3 [&_img]:block [&_img]:max-w-full [&_img]:h-auto"
                    dangerouslySetInnerHTML={{ __html: cleanDetailHtml }}
                  />
                ) : (
                  <p className="text-gray-600 leading-relaxed text-sm whitespace-pre-line">{displayDetailDesc}</p>
                )
              )}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col gap-2.5">
              <button
                onClick={onInquiryClick}
                className="w-full flex items-center justify-center gap-2 bg-[#004A99] hover:bg-[#003580] text-white py-3.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-customer-service-2-line" />
                {t("product.contactConsultation")}
              </button>
              {product.originalLink && (
                <a
                  href={product.originalLink}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-600 py-3 rounded-xl font-semibold text-sm hover:border-[#004A99]/30 hover:text-[#004A99] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line" />
                  {t("product.viewCompanyWebsite")}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductOverview;